import { dispatchCustomEvent } from "@/shared/utils/dom";
import { CUSTOM_EVENT_NEW_THEME_APPLIED } from "@/shared/utils/theme/constants";

const packageJson = require("../../../main/package.json");

export const DefaultFontSize = 18;

export const COOKIE_KEY_THEME = "theme";
export const COOKIE_KEY_VIEWMODE = "viewmode";
export const COOKIE_KEY_FULLSCREEN = "fullScreen";
export const COOKIE_KEY_READER_FONT_SIZE = "rfs";
export const COOKIE_KEY_SEARCH_FONT_SIZE = "sfs";

export const ThemeLocations = {
  MAIN_APP: "MAIN_APP",
  NEXT_APP: "NEXT_APP",
  LANDING_APP: "LANDING_APP",
};

export const Theme = {
  dy: {
    id: "dy",
    label: "dayMode",
    gifLoader: "loader-dy.gif",
    location: [ThemeLocations.MAIN_APP, ThemeLocations.NEXT_APP],
  },
  dw: {
    id: "dw",
    label: "dawnMode",
    gifLoader: "loader-dw.gif",
    location: [ThemeLocations.MAIN_APP, ThemeLocations.NEXT_APP],
  },
  dk: {
    id: "dk",
    label: "duskMode",
    dark: true,
    gifLoader: "loader-dk.gif",
    location: [ThemeLocations.MAIN_APP, ThemeLocations.NEXT_APP],
  },
  dm: {
    id: "dm",
    label: "darkMode",
    dark: true,
    gifLoader: "loader-dm.gif",
    location: [ThemeLocations.MAIN_APP, ThemeLocations.NEXT_APP],
  },
  dg: {
    id: "dg",
    label: "darkGreen",
    dark: false,
    gifLoader: "loader-dy.gif",
    location: [ThemeLocations.LANDING_APP],
  },
  dgd: {
    id: "dgd",
    label: "darkGreenDarken",
    dark: true,
    gifLoader: "loader-dm.gif",
    location: [ThemeLocations.LANDING_APP],
  },
  ls: {
    id: "ls",
    label: "landingSDA",
    dark: false,
    gifLoader: "loader-dy.gif",
    location: [ThemeLocations.LANDING_APP],
  },
  adm: {
    id: "adm",
    label: "accessabilityDarkMode",
    dark: true,
    accessibility: true,
    gifLoader: "loader-adm.gif",
    location: [ThemeLocations.MAIN_APP, ThemeLocations.NEXT_APP],
  },
  ady: {
    id: "ady",
    label: "accessabilityDayMode",
    accessibility: true,
    gifLoader: "loader-dy.gif",
    location: [ThemeLocations.MAIN_APP, ThemeLocations.NEXT_APP],
  },
};

export const MapIdThemeLightToIdThemeDark = {
  [Theme.dy.id]: Theme.dk.id,
  [Theme.dw.id]: Theme.dm.id,
  [Theme.ady.id]: Theme.adm.id,
  [Theme.dg.id]: Theme.dgd.id,
};

export const MapIdThemeDarkToIdThemeLight = {};

for (const key in MapIdThemeLightToIdThemeDark) {
  MapIdThemeDarkToIdThemeLight[MapIdThemeLightToIdThemeDark[key]] = key;
}

export const Themes = Object.values(Theme);

export const DefaultThemeId = Theme.dy.id;
export const TextappDefaultThemeId = Theme.dw.id;

export const hexToRgb = (hex) => {
  if (hex.slice(0, 1) === "#") {
    hex = hex.slice(1);
  }

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((hex) => hex + hex)
      .join("");
  }

  const red = parseInt(hex.substr(0, 2), 16);
  const green = parseInt(hex.substr(2, 2), 16);
  const blue = parseInt(hex.substr(4, 2), 16);

  return [red, green, blue];
};

export const getIsAccessibilityTheme = (theme) => {
  return Theme[theme] ? !!Theme[theme].accessibility : undefined;
};

export const isDarkTheme = (theme) => {
  return Theme[theme] && Theme[theme].dark;
};

export const checkColorDark = (hex) => {
  const [red, green, blue] = hexToRgb(hex);
  return 1 - (0.299 * red + 0.587 * green + 0.114 * blue) / 255 > 0.5;
};

export const isThemeValid = (idTheme, location) => {
  return Theme[idTheme] && Theme[idTheme].location.includes(location);
};

export const ReaderFontSizes = {
  min: 8,
  max: 72,
  factor: 2,
  default: 20,
  blindDefault: 36,
};

export const getTheme = (idTheme) => {
  let theme = Theme[idTheme];
  if (theme) {
    return theme;
  }
  return Theme[DefaultThemeId];
};

const checkTimeout = 145;
const onLoadCSSLinkElement = function (link, callback, attempt = 0) {
  let cssLoaded = false;
  try {
    if (link.sheet && link.sheet.cssRules.length > 0) {
      cssLoaded = true;
    } else if (link.styleSheet && link.styleSheet.cssText.length > 0) {
      cssLoaded = true;
    } else if (link.innerHTML && link.innerHTML.length > 0) {
      cssLoaded = true;
    }
  } catch (ex) {
    // case for el being removed or some cross-browser issue.
  }
  if (cssLoaded) {
    callback();
  } else {
    if ((attempt * checkTimeout) / 1000 < 25) {
      // prevent unexpected cases when no load more than 25s
      setTimeout(() => {
        onLoadCSSLinkElement(link, callback, ++attempt);
      }, checkTimeout);
    }
  }
};

const classNameThemeLink = "theme-link";

// creates new <link> with theme css url, removes old one after the new is loaded.
export const activateTheme = (themeMode) => {
  // consoleLogInfo("Theme", "Activate: ", themeMode);
  const themeIdNew = getTheme(themeMode).id;
  const elementsThemeLink = document.querySelectorAll("." + classNameThemeLink);

  const themeLinkEl = document.createElement("link");
  themeLinkEl.rel = "stylesheet";
  themeLinkEl.className = classNameThemeLink;
  themeLinkEl.href = window.location.origin + "/styles/" + themeIdNew + ".css?v=" + packageJson.version;

  document.head.appendChild(themeLinkEl);

  onLoadCSSLinkElement(themeLinkEl, () => {
    elementsThemeLink.forEach((el) => {
      if (el) {
        el.remove();
      }
    });

    dispatchCustomEventNewThemeApplied();
  });
};

export const dispatchCustomEventNewThemeApplied = () => {
  dispatchCustomEvent(CUSTOM_EVENT_NEW_THEME_APPLIED);
};

export const getRootCSSVariable = (name) => {
  return getComputedStyle(document.documentElement).getPropertyValue("--" + name);
};

export const getElementCSSVariable = (element, name) => {
  return element.style.getPropertyValue("--" + name);
};

export const getContrastColor = (color) => {
  return checkColorDark(color) ? getRootCSSVariable("hl-light") : getRootCSSVariable("hl-dark");
};

export const checkColorLight = (hex) => {
  const [red, green, blue] = hexToRgb(hex);
  return red > 240 && green > 240 && blue > 240;
};

export const fillContrastColor = (color) => {
  const backgroundColor = getRootCSSVariable("tree-row-odd");

  const lightAndDark = color === "#000000" || checkColorLight(color);

  if (lightAndDark) {
    return getContrastColor(backgroundColor);
  }

  return color;
};

/**
 * Return contrast text color for hightlight
 * @param {String} hexcolor
 * @returns css variable for color
 */
export const getContrastTextColor = (hexcolor) => {
  return checkColorDark(hexcolor) ? "var(--hl-light)" : "var(--hl-dark)";
};
